import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Check, ChevronsUpDown, Search } from 'lucide-react';
import { Button } from './ui/button';
import { ClinicLabel, useIsInternalUser } from './ClinicLabel';
import { Popover, PopoverContent, PopoverTrigger } from './ui/popover';
import { cn } from '@/lib/utils';
import type { ClinicResponse } from '@/api/hospital';

/**
 * Searchable single-select clinic picker. Mirrors v1's `<Typeahead>` from
 * the addUser modal — clinicians with many clinics can type to narrow the
 * list rather than scrolling a long select.
 *
 * Built on the existing Popover primitive so it slots in next to the
 * existing form controls without a new third-party dep.
 */

type Props = {
  clinics: ClinicResponse[];
  value: number | null;
  onChange: (clinicId: number) => void;
  placeholder?: string;
  disabled?: boolean;
  loading?: boolean;
  /** When true, the popover trigger receives initial focus on mount.
   *  Mirrors the React `autoFocus` semantic on inputs. */
  autoFocus?: boolean;
};

export const ClinicCombobox = ({ clinics, value, onChange, placeholder, disabled, loading, autoFocus }: Props) => {
  const { t } = useTranslation();
  const isInternal = useIsInternalUser();
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);

  // Reset filter every time the popover closes so the next open is fresh.
  useEffect(() => {
    if (!open) setQuery('');
  }, [open]);

  // Sort case-insensitive then filter by substring. Single source of truth
  // so the empty-state message is accurate (`No matches` only when the
  // filter is non-empty AND yields nothing).
  const sorted = useMemo(
    () => [...clinics].sort((a, b) => (a.name ?? '').localeCompare(b.name ?? '', undefined, { sensitivity: 'base' })),
    [clinics],
  );
  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return sorted;
    // Match on clinic name, plus id for internal (RxFood) users only — they're
    // the ones who see the "(#id)" suffix, so id-search stays hidden from
    // non-internal clinicians (mirrors the patients-page filter).
    return sorted.filter(
      (c) => c.name.toLowerCase().includes(q) || (isInternal && String(c.id).includes(q)),
    );
  }, [sorted, query, isInternal]);

  const selected = clinics.find((c) => c.id === value);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="outline"
          role="combobox"
          aria-expanded={open}
          autoFocus={autoFocus}
          disabled={disabled || loading}
          className={cn('w-full justify-between font-normal', !selected && 'text-muted-foreground')}
        >
          <span className="truncate">
            {loading ? (
              t('Loading…')
            ) : selected ? (
              <ClinicLabel name={selected.name} id={selected.id} />
            ) : (
              placeholder ?? t('Select a clinic')
            )}
          </span>
          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[var(--radix-popover-trigger-width)] p-0" align="start">
        <div className="border-b p-2">
          <div className="relative">
            <Search className="pointer-events-none absolute left-2 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" />
            <input
              ref={inputRef}
              type="text"
              autoFocus
              placeholder={t('Search clinics')}
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              className="h-8 w-full rounded-md border border-input bg-background pl-7 pr-2 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            />
          </div>
        </div>
        <div className="max-h-64 overflow-y-auto p-1">
          {filtered.length === 0 ? (
            <p className="px-3 py-4 text-center text-xs text-muted-foreground">{t('No matches')}</p>
          ) : (
            filtered.map((c) => {
              const isSelected = c.id === value;
              return (
                <button
                  key={c.id}
                  type="button"
                  onClick={() => {
                    onChange(c.id);
                    setOpen(false);
                  }}
                  className={cn(
                    'flex w-full items-center justify-between gap-2 rounded px-2 py-1.5 text-left text-sm hover:bg-muted',
                    isSelected && 'bg-muted/50 font-medium',
                  )}
                >
                  <ClinicLabel name={c.name} id={c.id} className="truncate" />
                  {isSelected && <Check className="h-3.5 w-3.5 text-primary" />}
                </button>
              );
            })
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
};
